import { useState, useEffect, useCallback, ReactNode } from 'react';

/**
 * Props for the CollapsibleSidebar component.
 */
interface CollapsibleSidebarProps {
    /** Content to render inside the sidebar */
    children: ReactNode;
    /** Width of the sidebar when expanded (default: 'w-64') */
    width?: string;
    /** Whether the sidebar is open by default on desktop (default: true) */
    defaultOpenDesktop?: boolean;
    /** Whether the sidebar is open by default on mobile (default: false) */
    defaultOpenMobile?: boolean;
    /** Additional CSS classes for the sidebar container */
    className?: string;
    /** Background color class (default: 'bg-slate-800') */
    bgColor?: string;
    /** Border color class (default: 'border-slate-700') */
    borderColor?: string;
    /** Position of the toggle button: 'inside' or 'outside' (default: 'outside') */
    togglePosition?: 'inside' | 'outside';
    /** Custom toggle button styling */
    toggleClassName?: string;
    /** Aria label for the toggle button */
    toggleAriaLabel?: string;
}

/**
 * Breakpoint for mobile detection (matches Tailwind's 'md' breakpoint).
 */
const MOBILE_BREAKPOINT = 768;

/**
 * CollapsibleSidebar - A responsive sidebar component that can be collapsed/expanded.
 * 
 * Features:
 * - Hidden by default on mobile, visible by default on desktop
 * - Overlays content on mobile when open
 * - Pushes content on desktop when open
 * - Accessible toggle button with visual state indicator
 * - Smooth transitions
 */
export function CollapsibleSidebar({
    children,
    width = 'w-64',
    defaultOpenDesktop = true,
    defaultOpenMobile = false,
    className = '',
    bgColor = 'bg-slate-800',
    borderColor = 'border-slate-700',
    togglePosition = 'outside',
    toggleClassName = '',
    toggleAriaLabel = 'Toggle sidebar',
}: CollapsibleSidebarProps) {
    // Detect if we're on mobile
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth < MOBILE_BREAKPOINT;
    });

    // Sidebar open state - depends on viewport size
    const [isOpen, setIsOpen] = useState(() => {
        if (typeof window === 'undefined') return defaultOpenDesktop;
        return window.innerWidth < MOBILE_BREAKPOINT ? defaultOpenMobile : defaultOpenDesktop;
    });

    /**
     * Handle window resize to update mobile state and sidebar visibility.
     */
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            const wasMobile = isMobile;
            setIsMobile(mobile);

            // When transitioning between mobile and desktop, reset to defaults
            if (mobile !== wasMobile) {
                setIsOpen(mobile ? defaultOpenMobile : defaultOpenDesktop);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobile, defaultOpenDesktop, defaultOpenMobile]);

    /**
     * Toggle sidebar open/closed state.
     */
    const toggleSidebar = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    /**
     * Close sidebar (used for overlay click on mobile).
     */
    const closeSidebar = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Extract numeric width for positioning (e.g., 'w-64' -> 64 -> 16rem)
    const widthMatch = width.match(/w-(\d+)/);
    const widthValue = widthMatch ? parseInt(widthMatch[1], 10) * 0.25 : 16; // Convert to rem

    return (
        <>
            {/* Mobile overlay backdrop - only shown when sidebar is open on mobile */}
            {isMobile && isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={closeSidebar}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar container */}
            <aside
                className={`
                    ${isOpen ? width : 'w-0'}
                    ${bgColor}
                    border-r ${borderColor}
                    flex-shrink-0
                    overflow-hidden
                    transition-all duration-300 ease-in-out
                    ${isMobile ? 'fixed inset-y-0 left-0 z-50' : 'relative'}
                    ${className}
                `}
            >
                {/* Inner container with fixed width to prevent content reflow */}
                <div className={`${width} h-full overflow-y-auto`}>
                    {children}
                </div>
            </aside>

            {/* Toggle button */}
            <button
                onClick={toggleSidebar}
                className={`
                    ${togglePosition === 'outside' ? 'absolute' : 'relative'}
                    z-50
                    p-2
                    ${bgColor}
                    border ${borderColor}
                    rounded
                    text-gray-300 hover:text-white
                    hover:bg-slate-700
                    transition-all duration-300
                    focus:outline-none focus:ring-2 focus:ring-purple-500
                    ${toggleClassName}
                `}
                style={togglePosition === 'outside' ? {
                    top: '1rem',
                    left: isOpen ? `calc(${widthValue}rem + 0.5rem)` : '0.5rem',
                } : undefined}
                aria-label={toggleAriaLabel}
                aria-expanded={isOpen}
            >
                {/* Hamburger / Close icon */}
                <span className="block w-5 h-5 relative">
                    <span
                        className={`
                            absolute left-0 w-5 h-0.5 bg-current
                            transition-all duration-300
                            ${isOpen ? 'top-2 rotate-45' : 'top-1'}
                        `}
                    />
                    <span
                        className={`
                            absolute left-0 top-2 w-5 h-0.5 bg-current
                            transition-all duration-300
                            ${isOpen ? 'opacity-0' : 'opacity-100'}
                        `}
                    />
                    <span
                        className={`
                            absolute left-0 w-5 h-0.5 bg-current
                            transition-all duration-300
                            ${isOpen ? 'top-2 -rotate-45' : 'top-3'}
                        `}
                    />
                </span>
            </button>
        </>
    );
}

/**
 * Hook to manage sidebar state externally if needed.
 * Useful when parent components need to control sidebar state.
 */
export function useSidebarState(defaultOpenDesktop = true, defaultOpenMobile = false) {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth < MOBILE_BREAKPOINT;
    });

    const [isOpen, setIsOpen] = useState(() => {
        if (typeof window === 'undefined') return defaultOpenDesktop;
        return window.innerWidth < MOBILE_BREAKPOINT ? defaultOpenMobile : defaultOpenDesktop;
    });

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            const wasMobile = isMobile;
            setIsMobile(mobile);

            if (mobile !== wasMobile) {
                setIsOpen(mobile ? defaultOpenMobile : defaultOpenDesktop);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobile, defaultOpenDesktop, defaultOpenMobile]);

    const toggle = useCallback(() => setIsOpen(prev => !prev), []);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    return { isOpen, isMobile, toggle, open, close, setIsOpen };
}

